import {inject, Injectable} from '@angular/core';
import {NavigationExtras, Router} from '@angular/router';
import {HttpClient, HttpResponse, HttpStatusCode} from '@angular/common/http';
import {BehaviorSubject, finalize, from, map, Observable, of, switchMap, take, tap} from 'rxjs';
import {environment} from '@environments/environment';
import {catchError} from 'rxjs/operators';
import {TranslocoService} from '@jsverse/transloco';
import {BrowserService} from '@core/service/browser.service';
import {NotificationService} from '@shared/notification/notification.service';
import {UserApi} from '@domain/user/user.api';
import {User} from '@domain/user/user';
import {
  LoginRequest,
  RegisterRequest,
  ResetPasswordRequest,
  SendResetPasswordEmailRequest,
  ValidateEmailRequest,
  ValidationEmailRequest
} from '@core/auth/auth.interfaces';
import {ApplicationLang} from '@domain/common/common';
import {RouteGenerator} from '@core/route-generator';
import {UI_ROUTES} from '@core/ui.routes';
import {CheckIcon} from 'lucide-angular';
import {UmamiService} from '@core/service/umami.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private readonly $authenticatedUser = new BehaviorSubject<User | null>(null);

  // public readonly INITIAL_PATH = "/app/dashboard";
  // public readonly ADMIN_PATH = "/admin";
  public static readonly API_LOGIN = '/auth/login';
  private readonly API_REGISTER = '/auth/register';
  private readonly API_VALIDATE_EMAIL = '/auth/validate-email';
  private readonly API_VALIDATION_EMAIL = '/auth/validation-email';
  private readonly API_RESET_PASSWORD_EMAIL = '/auth/reset-password-email';
  private readonly API_RESET_PASSWORD = '/auth/reset-password';
  // private readonly API_SIGNUP = '/security/register';
  private readonly API_LOGOUT = '/auth/logout';
  private readonly API_ME = '/auth/me';

  private readonly ROUTE_DASHBOARD = RouteGenerator.abs(UI_ROUTES.account.dashboard);
  private readonly ROUTE_GUEST_DASHBOARD = RouteGenerator.abs(UI_ROUTES.public.dashboard);
  private readonly ROUTE_LOGIN = RouteGenerator.abs(UI_ROUTES.auth.login);

  private readonly umamiService = inject(UmamiService);

  constructor(
    private readonly router: Router,
    private readonly http: HttpClient,
    private readonly browserService: BrowserService,
    private readonly notifyService: NotificationService,
    private readonly transloco: TranslocoService,
    private readonly userApi: UserApi
  ) {}

  public getAuthenticatedUserUpdates(): Observable<User | null> {
    return this.$authenticatedUser.asObservable();
  }

  public getAuthenticatedUser(): User | null {
    return this.$authenticatedUser.value;
  }

  public redirectToLogin(): Observable<boolean> {
    return from(this.router.navigate([this.ROUTE_LOGIN]));
  }

  public redirectToGuestDashboard(): Observable<boolean> {
    return from(this.router.navigate([this.ROUTE_GUEST_DASHBOARD]));
  }

  public redirectToDashboard(): Observable<boolean> {
    return from(this.router.navigate([this.ROUTE_DASHBOARD]));
  }

  public refreshAuthenticatedUser(): Observable<User | null> {
    let isAuthenticated = !!this.browserService.read(BrowserService.STORAGE_KEY.IsAuthenticated);
    const $user = isAuthenticated
      ? this.http.get<User | null>(`${environment.apiUrl}${this.API_ME}`)
      : of (null);
    return $user.pipe(
      tap((user: User | null) => {
        isAuthenticated = !!user;
        // TODO: improve performances (deep compare users before sending a pulse)
        this.umamiService.identifyUser(user);
        this.$authenticatedUser.next(user);
        this.browserService.write(BrowserService.STORAGE_KEY.IsAuthenticated, (+isAuthenticated).toString(10));
      }),
      switchMap(user => user?.lang ? this.setLanguage(user.lang, false) : of(user))
    );
  }

  public login(
    loginRequest: LoginRequest,
    options?: {
      noRedirect?: boolean;
    }
  ): Observable<User | null> {
    return this.http.post<User>(`${environment.apiUrl}${AuthService.API_LOGIN}`, loginRequest).pipe(
      tap(user => {
        this.umamiService.identifyUser(user);
      }),
      switchMap(user => this.afterLogin(user, options))
    );
  }

  public register(registerRequest: RegisterRequest): Observable<HttpResponse<User>> {
    return this.http.post<User>(
      `${environment.apiUrl}${this.API_REGISTER}`,
      registerRequest,
      {observe: 'response'}
    );
  }

  public validateEmail(validateRequest: ValidateEmailRequest): Observable<boolean> {
    return this.http
      .post<void>(
        `${environment.apiUrl}${this.API_VALIDATE_EMAIL}`,
        validateRequest,
        {observe: 'response'}
      )
      .pipe(
        map(response => response?.status === HttpStatusCode.NoContent),
        switchMap(validated => validated ? this.refreshAuthenticatedUser().pipe(map(user => !!user)) : of(false)),
        catchError(_err => of(false)),
        take(1)
      );
  }

  public resendValidationEmail(request: ValidationEmailRequest): Observable<boolean> {
    return this.http
      .post<void>(
        `${environment.apiUrl}${this.API_VALIDATION_EMAIL}`,
        request,
        {observe: 'response'}
      )
      .pipe(
        map(response => response?.status === HttpStatusCode.NoContent),
        catchError(_err => of(false)),
        take(1)
      );
  }

  public logout(redirectToLogin = true): Observable<boolean> {
    return this.http
      .get<any>(`${environment.apiUrl}${this.API_LOGOUT}`)
      .pipe(
        tap(() => {
          this.$authenticatedUser.next(null);
          this.browserService.delete(BrowserService.STORAGE_KEY.IsAuthenticated);
          this.umamiService.clearUser();
        }),
        switchMap(_ => (
          redirectToLogin
            ? from(this.router.navigate([this.ROUTE_LOGIN]))
            : of(false)
        ))
      );
  }

  public sendResetPasswordEmail(request: SendResetPasswordEmailRequest): Observable<boolean> {
    return this.http
      .post<void>(
        `${environment.apiUrl}${this.API_RESET_PASSWORD_EMAIL}`,
        request,
        {observe: 'response'}
      )
      .pipe(
        map(response => response?.status === HttpStatusCode.NoContent),
        catchError(_err => of(false)),
        take(1),
        tap(emailSent => {
          if (emailSent) {
            this.notifyService.toast({
              message: this.transloco.translate('auth.login.ResetEmailSent'),
              icon: CheckIcon
            });
          }
        })
      );
  }

  public resetPassword(resetRequest: ResetPasswordRequest): Observable<User | null> {
    return this.http.post<User>(`${environment.apiUrl}${this.API_RESET_PASSWORD}`, resetRequest).pipe(
      tap(user => {
        if (user) {
          this.notifyService.toast({
            message: this.transloco.translate('auth.login.PasswordSet'),
            icon: CheckIcon
          });
        }
      }),
      switchMap(user => this.afterLogin(user))
    );
  }

  public setLanguage(lang: string, updateUser: boolean): Observable<User | null> {
    this.transloco.setActiveLang(lang);
    const user = this.getAuthenticatedUser();
    if (user) {
      const newLang: ApplicationLang = ApplicationLang[lang as keyof typeof ApplicationLang];
      user.lang = newLang;
      if (updateUser) {
        return this.userApi.update({
          id: user.id,
          lang: newLang
        }).pipe(
          take(1)
        );
      }
    }
    return of(user);
  }

  public afterLogin(
    user: User | null,
    options?: {
      noRedirect?: boolean;
      redirectTo?: {
        commands: any[];
        extras?: NavigationExtras;
      };
  }): Observable<User | null> {
    if (user !== this.$authenticatedUser.value) {
      this.$authenticatedUser.next(user || null);
    }
    return of(user).pipe(
      switchMap(user => user?.lang ? this.setLanguage(user.lang, false) : of(user)),
      finalize(() => {
        const user = this.$authenticatedUser.value;
        if (user) {
          this.browserService.write(BrowserService.STORAGE_KEY.IsAuthenticated, '1');
          if (!options?.noRedirect) {
            const navigationCommands = user
              ? (options?.redirectTo ? options.redirectTo.commands : [this.ROUTE_DASHBOARD])
              : [this.ROUTE_LOGIN];
            const navigationExtras = user && options?.redirectTo?.extras
              ? options.redirectTo.extras
              : undefined;
            this.router.navigate(navigationCommands, navigationExtras).then(_ => {});
          }
        } else {
          this.browserService.delete(BrowserService.STORAGE_KEY.IsAuthenticated);
        }
      })
    );
  }

}
